/* intakeDetect — typed detector for what the user just dropped.
 *
 * We *do not* claim "out-of-the-box Foyr Neo ingest". Foyr Neo's
 * export schema is not publicly documented; reading an unknown JSON
 * shape into a typed reasoning graph is gambling, and a wrong type
 * = a wrong rule firing = a wrong alert. So we route on shape, not
 * extension, and we tell the truth about confidence.
 *
 * Three honest paths:
 *
 *   - "brief"       — plain-language brief, costing rows, or any
 *                     free-text the LLM extractor maps with
 *                     confidence bands (CLAUDE.md §5 — surfaced).
 *
 *   - "json-heuristic" — known-shape JSON (rooms[] / items[] /
 *                        x/y/z placements). Pattern-matches field
 *                        names; ~60% of real exports, confidence
 *                        depends on overlap.
 *
 *   - "foyr-export" — *recognized* as a Foyr Neo scene bundle. We
 *                     don't yet have the typed parser; the path is
 *                     gated to a roadmap CTA until you share one
 *                     real export with the team.
 *
 * No literal magic numbers in the body — confidence floors come
 * from the typed env so they're tunable per cluster. */

import { env } from "./env";

export type IntakePathId = "brief" | "json-heuristic" | "foyr-export" | "unknown";

export interface IntakeDetection {
  path: IntakePathId;
  confidence: number; // 0..1
  reasons: string[];
  shape: {
    bytes: number;
    contentKind: "text" | "json" | "binary";
    knownFieldHits: string[];
    foyrSignals: string[];
  };
  /** True if we recognized the file but cannot reliably parse it
   *  today (e.g. Foyr Neo .foyr without a documented schema). */
  gated: boolean;
}

const JSON_FIELDS_OF_INTEREST: ReadonlyArray<{ field: string; weight: number }> = [
  { field: "rooms",       weight: 0.20 },
  { field: "items",       weight: 0.18 },
  { field: "placements",  weight: 0.12 },
  { field: "moodboard",   weight: 0.06 },
  { field: "budget",      weight: 0.10 },
  { field: "milestones",  weight: 0.08 },
  { field: "vendor",      weight: 0.04 },
  { field: "approvals",   weight: 0.06 },
  { field: "x",           weight: 0.04 },
  { field: "y",           weight: 0.04 },
  { field: "z",           weight: 0.04 },
  { field: "wallLength",  weight: 0.04 },
];

const FOYR_SIGNALS: ReadonlyArray<{ rx: RegExp; reason: string }> = [
  { rx: /foyr/i,                  reason: "Filename / payload mentions Foyr" },
  { rx: /\.foyr\b/i,              reason: "Foyr scene extension" },
  { rx: /\"sceneId\"\s*:/i,       reason: "Foyr-style scene id key" },
  { rx: /\"floorPlanId\"\s*:/i,   reason: "Foyr-style floorPlanId key" },
  { rx: /catalogSku/i,            reason: "Foyr-style catalog SKU reference" },
];

/** Detect the right intake path from the dropped sample. Pure, no
 *  side effects — feed the first ~32 KB; the rest doesn't change
 *  the verdict and keeps the in-browser path cheap. */
export function detectIntakePath({
  filename,
  text,
  bytes,
}: {
  filename: string;
  text: string;
  bytes: number;
}): IntakeDetection {
  const reasons: string[] = [];
  const lower = (filename + " " + text.slice(0, 4096)).toLowerCase();

  // 1) Foyr-shape signal first (gated path).
  const foyrSignals: string[] = [];
  for (const sig of FOYR_SIGNALS) {
    if (sig.rx.test(filename) || sig.rx.test(text.slice(0, 4096))) {
      foyrSignals.push(sig.reason);
    }
  }

  // 2) Try JSON shape.
  let contentKind: IntakeDetection["shape"]["contentKind"] = "text";
  const knownFieldHits: string[] = [];
  let knownScore = 0;

  const looksJson = filename.endsWith(".json") || text.trim().startsWith("{") || text.trim().startsWith("[");
  if (looksJson) {
    try {
      const parsed: unknown = JSON.parse(text);
      contentKind = "json";
      for (const f of JSON_FIELDS_OF_INTEREST) {
        if (deepHasKey(parsed, f.field)) {
          knownFieldHits.push(f.field);
          knownScore += f.weight;
        }
      }
    } catch {
      reasons.push("Looked like JSON but did not parse — treating as free text.");
    }
  }

  // 3) Brief-shape — long-enough free text without JSON hits.
  const briefShape = !looksJson && text.replace(/\s+/g, " ").trim().length >= 80;

  // 4) Decision.
  const FLOOR = env.VITE_INTAKE_CONFIDENCE_FLOOR;

  if (foyrSignals.length >= 2) {
    // Strongly looks like Foyr — gate to the roadmap path so we
    // don't pretend to parse a schema we don't have.
    return {
      path: "foyr-export",
      confidence: Math.min(1, 0.4 + foyrSignals.length * 0.15),
      reasons: [
        ...foyrSignals.map((s) => `Detected: ${s}`),
        "Foyr Neo's export schema isn't publicly documented — we don't auto-ingest yet (see roadmap).",
      ],
      shape: { bytes, contentKind, knownFieldHits, foyrSignals },
      gated: true,
    };
  }

  if (contentKind === "json" && knownScore > 0) {
    const conf = Math.min(0.95, Math.max(FLOOR, knownScore));
    return {
      path: "json-heuristic",
      confidence: conf,
      reasons: [
        `Recognized typed fields: ${knownFieldHits.join(", ")}`,
        "Mapping uses confidence-gated rules — anything below the threshold becomes a clarifying question, never a silent default.",
      ],
      shape: { bytes, contentKind, knownFieldHits, foyrSignals },
      gated: false,
    };
  }

  if (briefShape) {
    return {
      path: "brief",
      confidence: 0.9,
      reasons: [
        "Looks like a written brief or costing sheet.",
        "The extractor maps to the ontology with one confidence band per field and asks only the low-confidence ones.",
      ],
      shape: { bytes, contentKind, knownFieldHits, foyrSignals },
      gated: false,
    };
  }

  return {
    path: "unknown",
    confidence: 0,
    reasons: [
      "Couldn't recognize this shape with high confidence.",
      "Paste a brief, drop a costing CSV, or share a JSON with at least one of: rooms[], items[], moodboard, budget.",
      ...(lower.includes("foyr") ? ["Looks Foyr-adjacent — see the Foyr Neo connector card."] : []),
    ],
    shape: { bytes, contentKind, knownFieldHits, foyrSignals },
    gated: false,
  };
}

function deepHasKey(obj: unknown, key: string, depth = 0): boolean {
  if (depth > 6 || obj === null || obj === undefined) return false;
  if (Array.isArray(obj)) return obj.some((v) => deepHasKey(v, key, depth + 1));
  if (typeof obj === "object") {
    const o = obj as Record<string, unknown>;
    if (key in o) return true;
    for (const k of Object.keys(o)) {
      if (deepHasKey(o[k], key, depth + 1)) return true;
    }
  }
  return false;
}
